import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../../providers/NotificationProvider";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function BscGoalListPage() {
  const { businessDomainId, businessUnitId, category } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${category}`;

  const config = BSC_CATEGORY_CONFIG[category];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const data = await config.fetchGoals(businessUnitId);
      setRows(Array.isArray(data) ? data.map((r, i) => ({ id: i, ...r })) : []);
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
  }, [businessUnitId, config]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!config) {
    return <Typography color="error">Unknown BSC category: {category}</Typography>;
  }

  const columns = [
    {
      field: "successFactorName",
      headerName: t("bsc.successFactor"),
      flex: 1,
      minWidth: 160,
    },
    {
      field: "indicatorName",
      headerName: t("bsc.indicator"),
      flex: 1,
      minWidth: 150,
      valueGetter: (v, row) => row.indicator?.indicatorName || "",
    },
    {
      field: "unit",
      headerName: t("bsc.unit"),
      width: 80,
      valueGetter: (v, row) => row.indicator?.unit || "",
    },
    {
      field: "plannedValue",
      headerName: t("bsc.plannedValue"),
      width: 110,
      valueGetter: (v, row) => row.goal?.plannedValue ?? "",
    },
    {
      field: "plannedDate",
      headerName: t("bsc.plannedDate"),
      width: 120,
      valueGetter: (v, row) => formatDate(row.goal?.plannedDate),
    },
    {
      field: "deadline",
      headerName: t("bsc.deadline"),
      width: 120,
      valueGetter: (v, row) => formatDate(row.goal?.deadline),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t(config.titleKey)}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/create`)}
        >
          {t("common.add")}
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        onRowClick={(params) => navigate(`${basePath}/${params.row.id}`)}
        sx={{ cursor: "pointer" }}
      />
    </Box>
  );
}
